import { useState, useRef, useCallback, useEffect } from "react";
import { MessageType } from "../views/Cart/components/GroupMessages/GroupMessages";

/**
 * Type definition for message object
 */
export interface CartMessage {
  id: string;
  type: MessageType;
  content: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  autoHide?: boolean;
  hideAfter?: number;
}

interface UseCartMessagesReturn {
  messages: CartMessage[];
  isExiting: boolean;
  showMessage: (message: CartMessage) => void;
  showSuccessMessage: (
    content: React.ReactNode,
    hideAfter?: number,
    customId?: string
  ) => string;
  showErrorMessage: (
    content: React.ReactNode,
    actions?: React.ReactNode,
    hideAfter?: number,
    customId?: string
  ) => string;
  showWarningMessage: (
    content: React.ReactNode,
    actions?: React.ReactNode,
    hideAfter?: number,
    customId?: string
  ) => string;
  showLoadingMessage: (
    content: React.ReactNode,
    autoHide?: boolean,
    hideAfter?: number,
    customId?: string
  ) => string;
  hideMessage: (id: string) => void;
  hideAllMessages: () => void;
  resetMessagesState: () => void;
  handleMessagesExited: () => void;
}

/**
 * Custom hook for managing cart messages with consistent state handling
 */
export function useCartMessages(): UseCartMessagesReturn {
  const [messages, setMessages] = useState<CartMessage[]>([]);
  const [isExiting, setIsExiting] = useState<boolean>(false);
  const activeMessageIds = useRef<Set<string>>(new Set());

  // Clean up messages when component unmounts
  useEffect(() => {
    return () => {
      activeMessageIds.current.clear();
    };
  }, []);

  /**
   * Shows a message of any type
   */
  const showMessage = useCallback((message: CartMessage) => {
    const { id } = message;

    // Don't add duplicate messages with same ID
    if (activeMessageIds.current.has(id)) return;

    // Add message ID to tracking set
    activeMessageIds.current.add(id);

    // Add new message to state
    setMessages((current) => [
      ...current.filter((msg) => msg.id !== id),
      message,
    ]);
    setIsExiting(false);
  }, []);

  /**
   * Shows a success message with default settings
   */
  const showSuccessMessage = useCallback(
    (content: React.ReactNode, hideAfter: number = 2000, customId?: string) => {
      const id = customId || `success-${Date.now()}`;

      // Clear any existing success messages to prevent stacking
      messages.forEach((msg) => {
        if (msg.type === MessageType.SUCCESS) {
          activeMessageIds.current.delete(msg.id);
        }
      });

      showMessage({
        id,
        type: MessageType.SUCCESS,
        content,
        autoHide: true,
        hideAfter,
      });
      return id;
    },
    [showMessage, messages]
  );

  /**
   * Shows an error message with default settings
   */
  const showErrorMessage = useCallback(
    (
      content: React.ReactNode,
      actions?: React.ReactNode,
      hideAfter: number = 3000,
      customId?: string
    ) => {
      const id = customId || `error-${Date.now()}`;

      // Clear any existing error messages with the same content to prevent stacking
      messages.forEach((msg) => {
        if (msg.type === MessageType.ERROR && msg.content === content) {
          activeMessageIds.current.delete(msg.id);
        }
      });

      showMessage({
        id,
        type: MessageType.ERROR,
        content,
        actions,
        autoHide: !!hideAfter,
        hideAfter,
      });
      return id;
    },
    [showMessage, messages]
  );

  /**
   * Shows a warning message with default settings
   */
  const showWarningMessage = useCallback(
    (
      content: React.ReactNode,
      actions?: React.ReactNode,
      hideAfter?: number,
      customId?: string
    ) => {
      const id = customId || `warning-${Date.now()}`;

      // Clear any existing warning messages with the same content
      messages.forEach((msg) => {
        if (msg.type === MessageType.WARNING && msg.content === content) {
          activeMessageIds.current.delete(msg.id);
        }
      });

      showMessage({
        id,
        type: MessageType.WARNING,
        content,
        actions,
        autoHide: !!hideAfter,
        hideAfter,
      });
      return id;
    },
    [showMessage, messages]
  );

  /**
   * Shows a loading message with default settings
   * Returns the ID for later reference
   */
  const showLoadingMessage = useCallback(
    (
      content: React.ReactNode,
      autoHide: boolean = false,
      hideAfter: number = 8000,
      customId?: string
    ) => {
      const id = customId || `loading-${Date.now()}`;

      // Clear any existing loading messages with the same content
      messages.forEach((msg) => {
        if (msg.type === MessageType.LOADING && msg.content === content) {
          activeMessageIds.current.delete(msg.id);
        }
      });

      showMessage({
        id,
        type: MessageType.LOADING,
        content,
        autoHide,
        hideAfter,
      });
      return id;
    },
    [showMessage, messages]
  );

  /**
   * Hides a specific message by ID
   */
  const hideMessage = useCallback((id: string) => {
    if (activeMessageIds.current.has(id)) {
      activeMessageIds.current.delete(id);
      setMessages((current) => current.filter((msg) => msg.id !== id));

      // If no messages left, set exiting flag
      if (activeMessageIds.current.size === 0) {
        setIsExiting(true);
      }
    }
  }, []);

  /**
   * Hides all current messages
   */
  const hideAllMessages = useCallback(() => {
    if (activeMessageIds.current.size > 0) {
      setIsExiting(true);
    }
  }, []);

  /**
   * Resets messages state to empty
   */
  const resetMessagesState = useCallback(() => {
    activeMessageIds.current.clear();
    setMessages([]);
    setIsExiting(false);
  }, []);

  /**
   * Handler for when message exit animations complete
   */
  const handleMessagesExited = useCallback(() => {
    resetMessagesState();
  }, [resetMessagesState]);

  return {
    messages,
    isExiting,
    showMessage,
    showSuccessMessage,
    showErrorMessage,
    showWarningMessage,
    showLoadingMessage,
    hideMessage,
    hideAllMessages,
    resetMessagesState,
    handleMessagesExited,
  };
}
